// @flow

import React from 'react';

import { isBrowser, isCrawler, isProduction } from '@zapier/common-utils';

import useABTest from '../../hooks/useABTest';

import type { Node } from 'react';
import type { UseQuery } from '../../hooks/useABTest/types';

type Props = {
  /**
   * Name of the test you want to pull the current user's variant for.
   */
  testName: string,
  /**
   * An object containing keys that match the possible variants of the selected
   * AB Test. The value of these entries must be functions that return React
   * components.
   */
  variantRenderers: {
    control: () => Node,
    [string]: () => Node,
  },
  /**
   * Optional loading component to be displayed whenever the `useABTest` query
   * is retrieving data from our APIs.
   */
  LoadingComponent?: () => Node,
  /**
   * If this is `false`, it will disable the logging of the birth event.
   *
   * Note: You will need to implement your own birth event if you opt-out
   * of this behaviour. If you are unsure about this, it is recommended
   * you leave this `true`.
   */
  shouldTrackBirthEvent: boolean,
  /**
   * Optional metadata to send along with the birth event.
   */
  birthEventMetadata: {},
  /**
   * Optional useQuery hook to pass in for newer versions of Apollo.
   */
  useQuery?: UseQuery,
};

/**
 * Allows a consumer to run a client-side AB test with one or more variations.
 */
const ABTest = (props: Props) => {
  const {
    LoadingComponent,
    birthEventMetadata,
    shouldTrackBirthEvent,
    testName,
    useQuery,
    variantRenderers,
  } = props;

  if (!variantRenderers || !variantRenderers.control) {
    throw new Error(
      'The <ABTest> component requires a `control` component in order to render.'
    );
  }

  const { variant, isLoading } = useABTest(testName, {
    birthEventMetadata,
    shouldTrackBirthEvent,
    useQuery,
  });

  // If the client is detected as being a non-browser environment (e.g. SSR)
  // or is a known crawler (e.g. GoogleBot) then we return the `control` value
  // immediately. We never want crawlers or SSR to render anything but control
  // as this can have unwanted side effects on our SEO ranking.
  if (!isBrowser() || !isCrawler()) {
    return variantRenderers.control();
  }

  if (isLoading && LoadingComponent) {
    return <LoadingComponent />;
  }

  if (variantRenderers[variant]) {
    return variantRenderers[variant]();
  }

  // If we end up here, our consumer didn't provide a valid component for the
  // `variant` value we received from the API. In this case, let's show a
  // developer-friendly message so we can catch these problems in development.
  if (!isProduction()) {
    return (
      <div>
        {`Warning! The "${testName}" AB test encountered an error. There was no component provided for the "${variant}" variant.`}
      </div>
    );
  }

  // Fallbacks to `control` component if something goes awry in production.
  return variantRenderers.control();
};

ABTest.defaultProps = {
  birthEventMetadata: {},
  shouldTrackBirthEvent: true,
};

export default ABTest;
